import { useEffect, useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Button from './Button'
import Dialog from './Dialog'
import { FormMessage, Select } from './FormControls'
import { formatMoney, v1API, type PersonShareOverview } from '../lib/ledger'

const styles = stylex.create({
    body: { display: 'flex', flexDirection: 'column', gap: '18px' },
    section: { display: 'flex', flexDirection: 'column', gap: '10px' },
    label: { fontSize: '12px', fontWeight: 700, color: '#3f4b42' },
    copy: { fontSize: '12px', lineHeight: '1.25rem', color: '#748077' },
    option: { display: 'flex', alignItems: 'flex-start', gap: '10px', borderRadius: '0.75rem', borderWidth: 1, borderColor: '#e2e8df', backgroundColor: '#fbfdfb', padding: '11px 12px' },
    checkbox: { marginTop: '2px', height: '16px', width: '16px', accentColor: '#438b57' },
    optionTitle: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#344139' },
    optionCopy: { display: 'block', marginTop: '2px', fontSize: '11px', lineHeight: '1rem', color: '#7a857d' },
    linkBox: { borderRadius: '0.75rem', borderWidth: 1, borderColor: '#dce6d9', backgroundColor: '#f4f8f2', padding: '12px' },
    link: { overflowWrap: 'anywhere', fontSize: '12px', lineHeight: '1.2rem', color: '#355c40' },
    actions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    requests: { display: 'flex', flexDirection: 'column', gap: '8px' },
    request: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', borderRadius: '0.75rem', backgroundColor: '#fff8e8', padding: '10px 12px' },
    requestMain: { minWidth: '150px', flex: '1 1 0%' },
    requestTitle: { fontSize: '12px', fontWeight: 700, color: '#6f5720' },
    requestMeta: { marginTop: '2px', fontSize: '11px', lineHeight: '1rem', color: '#87703c' },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
})

type Props = {
    personID: string
    personName: string
    onClose: () => void
    onSettlementConfirmed?: () => Promise<void>
}

export default function PersonShareDialog({ personID, personName, onClose, onSettlementConfirmed }: Props) {
    const [overview, setOverview] = useState<PersonShareOverview | null>(null)
    const [showBalances, setShowBalances] = useState(true)
    const [showExpenses, setShowExpenses] = useState(true)
    const [showPayments, setShowPayments] = useState(true)
    const [showLoans, setShowLoans] = useState(true)
    const [expiry, setExpiry] = useState('never')
    const [createdURL, setCreatedURL] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')

    async function refresh() {
        setLoading(true)
        setError('')
        try {
            setOverview(await v1API.getPersonShare(personID))
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not load sharing settings.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        let active = true
        void v1API.getPersonShare(personID)
            .then((result) => {
                if (active) setOverview(result)
            })
            .catch((reason) => {
                if (active) setError(reason instanceof Error ? reason.message : 'Could not load sharing settings.')
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [personID])

    async function createLink() {
        if (!showBalances && !showExpenses && !showPayments && !showLoans) {
            setError('Choose at least one section to share.')
            return
        }
        setSaving(true)
        setError('')
        setMessage('')
        try {
            const expiresInDays = expiry === 'never' ? undefined : Number(expiry)
            const created = await v1API.createPersonShare(personID, {
                showBalances,
                showExpenses,
                showPayments,
                showLoans,
                ...(expiresInDays ? { expiresInDays } : {}),
            })
            const url = `${window.location.origin}/person-share/${created.token}`
            setCreatedURL(url)
            setOverview(await v1API.getPersonShare(personID))
            setMessage('A new private share link was created. The previous active link, if any, was disabled.')
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not create the share link.')
        } finally {
            setSaving(false)
        }
    }

    async function copyLink() {
        if (!createdURL) return
        await navigator.clipboard.writeText(createdURL)
        setMessage('Share link copied.')
    }

    async function revokeLink() {
        if (!overview?.activeLink) return
        setSaving(true)
        setError('')
        try {
            await v1API.revokePersonShare(overview.activeLink.id)
            setCreatedURL('')
            setMessage('Share link disabled.')
            await refresh()
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not disable the share link.')
        } finally {
            setSaving(false)
        }
    }

    async function confirmRequest(requestID: string, amount: string) {
        if (saving || !window.confirm(`Confirm that you received ${amount}? This will update the real balance.`)) return
        setSaving(true)
        setError('')
        setMessage('')
        try {
            await v1API.confirmGuestSettlementRequest(requestID)
            if (onSettlementConfirmed) await onSettlementConfirmed()
            await refresh()
            setMessage(`${amount} settlement approved. The balance was updated.`)
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not approve the settlement request.')
        } finally {
            setSaving(false)
        }
    }

    async function rejectRequest(requestID: string) {
        if (saving) return
        const reason = window.prompt('Optional reason for rejecting this request:', 'Payment not received')
        if (reason === null) return
        setSaving(true)
        setError('')
        setMessage('')
        try {
            await v1API.rejectGuestSettlementRequest(requestID, reason)
            await refresh()
            setMessage('Settlement request rejected.')
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : 'Could not reject the settlement request.')
        } finally {
            setSaving(false)
        }
    }

    const active = overview?.activeLink
    return <Dialog
        closeDisabled={saving}
        description={`Create a private read-only debt overview for ${personName}. Private notes, email and phone are never shared.`}
        footer={<div {...stylex.props(styles.footer)}><Button disabled={saving} onClick={onClose} variant="secondary">Close</Button><Button disabled={saving || loading} onClick={() => void createLink()}>{saving ? 'Working…' : active ? 'Create new link' : 'Create share link'}</Button></div>}
        onClose={onClose}
        title={`Share ${personName}'s overview`}
    >
        <div {...stylex.props(styles.body)}>
            {loading && <FormMessage>Loading sharing settings…</FormMessage>}
            {error && <FormMessage tone="error">{error}</FormMessage>}
            {message && <FormMessage tone="success">{message}</FormMessage>}

            {(overview?.requests ?? []).some((request) => request.status === 'pending') && <section {...stylex.props(styles.section)}>
                <span {...stylex.props(styles.label)}>Settlement requests</span>
                <p {...stylex.props(styles.copy)}>Requests from the shared page do not change the debt until you approve them.</p>
                <div {...stylex.props(styles.requests)}>{overview?.requests.filter((request) => request.status === 'pending').map((request) => {
                    const amount = formatMoney(request.amountCents, request.currency)
                    return <div {...stylex.props(styles.request)} key={request.id}>
                        <div {...stylex.props(styles.requestMain)}>
                            <p {...stylex.props(styles.requestTitle)}>{personName} says they paid {amount}</p>
                            <p {...stylex.props(styles.requestMeta)}>{request.note ? `${request.note} · ` : ''}${new Date(request.requestedAt).toLocaleString()}</p>
                        </div>
                        <Button disabled={saving} onClick={() => void rejectRequest(request.id)} size="sm" variant="ghost">Reject</Button>
                        <Button disabled={saving} onClick={() => void confirmRequest(request.id, amount)} size="sm">Confirm</Button>
                    </div>
                })}</div>
            </section>}

            {active && <section {...stylex.props(styles.section)}>
                <span {...stylex.props(styles.label)}>Active link</span>
                <p {...stylex.props(styles.copy)}>Created {new Date(active.createdAt).toLocaleString()}{active.expiresAt ? ` · expires ${new Date(active.expiresAt).toLocaleDateString()}` : ' · never expires'}.</p>
                <div {...stylex.props(styles.actions)}><Button disabled={saving} onClick={() => void revokeLink()} size="sm" variant="secondary">Disable active link</Button></div>
                {!createdURL && <p {...stylex.props(styles.copy)}>For security, the raw token is only shown when created. Create a new link if you need to copy it again.</p>}
            </section>}

            {createdURL && <section {...stylex.props(styles.section)}>
                <span {...stylex.props(styles.label)}>New share link</span>
                <div {...stylex.props(styles.linkBox)}><p {...stylex.props(styles.link)}>{createdURL}</p></div>
                <div {...stylex.props(styles.actions)}><Button onClick={() => void copyLink()} size="sm">Copy link</Button></div>
            </section>}

            <section {...stylex.props(styles.section)}>
                <span {...stylex.props(styles.label)}>What can they see?</span>
                <label {...stylex.props(styles.option)}><input checked={showBalances} {...stylex.props(styles.checkbox)} onChange={(event) => setShowBalances(event.target.checked)} type="checkbox" /><span><span {...stylex.props(styles.optionTitle)}>Current balances</span><span {...stylex.props(styles.optionCopy)}>Amount owed, payment claims in progress and remaining after pending claims.</span></span></label>
                <label {...stylex.props(styles.option)}><input checked={showExpenses} {...stylex.props(styles.checkbox)} onChange={(event) => setShowExpenses(event.target.checked)} type="checkbox" /><span><span {...stylex.props(styles.optionTitle)}>Shared expenses</span><span {...stylex.props(styles.optionCopy)}>Only expenses that directly changed the balance between you and this person.</span></span></label>
                <label {...stylex.props(styles.option)}><input checked={showPayments} {...stylex.props(styles.checkbox)} onChange={(event) => setShowPayments(event.target.checked)} type="checkbox" /><span><span {...stylex.props(styles.optionTitle)}>Payments</span><span {...stylex.props(styles.optionCopy)}>Confirmed settlements between you and this person.</span></span></label>
                <label {...stylex.props(styles.option)}><input checked={showLoans} {...stylex.props(styles.checkbox)} onChange={(event) => setShowLoans(event.target.checked)} type="checkbox" /><span><span {...stylex.props(styles.optionTitle)}>Loans</span><span {...stylex.props(styles.optionCopy)}>Outstanding loan balance and due date when a loan exists between you.</span></span></label>
            </section>

            <section {...stylex.props(styles.section)}>
                <label {...stylex.props(styles.label)} htmlFor="person-share-expiry">Link expiry</label>
                <Select id="person-share-expiry" onChange={(event) => setExpiry(event.target.value)} value={expiry}>
                    <option value="never">Never</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="365">1 year</option>
                </Select>
            </section>
        </div>
    </Dialog>
}
